import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
import SourcePicker from '../components/ShipmentPlannerV2/SourcePicker';
import LinesTable from '../components/ShipmentPlannerV2/LinesTable';
import LineActions from '../components/ShipmentPlannerV2/LineActions';
import OptionsPanel from '../components/ShipmentPlannerV2/OptionsPanel';
import BoxConfigModal from '../components/BoxConfigModal';
import { useBoxConfig } from '../hooks/useBoxConfig';
import { splitPoLine, refillItem, rederiveSpread } from '../utils/spreadImport';
import {
  isPackagingComplete, fmtLbsShort, computeLoadedBoxWeightLbs, deriveLegacyPackagingFields,
  computeShipmentTotals, boxFormFromCatalogBox, boxCatalogErrorText, unitBoxEntryFromDerived,
  unitBoxMatchesDerived, defaultPackagingStyle, packagingSummaryLine, packagingEntryFromRow,
  isAllowedModesSet, normalizeAllowedModes,
} from './ShipmentPlanner';

const EMPTY_MAP = {};
const NOOP = () => {};

const modeForRow = (row) => {
  if (!row || !isAllowedModesSet(row.allowedModes)) return { both: false, mode: 'UPS' };
  const d = normalizeAllowedModes(row.allowedModes);
  return d === 'BOTH' ? { both: true, mode: null } : { both: false, mode: d };
};

async function fetchPackaging(asins) {
  if (!asins.length) return {};
  const res = await ApiClient.get(`/api/shipment-planner/packaging?asins=${asins.map(encodeURIComponent).join(',')}`);
  const out = {};
  (res.data || []).forEach((r) => { out[r.asin] = r; });
  return out;
}

// Why a refill link has no line (final review F6): an unsizable product says what to fix, never "nothing to refill".
function refillMessage(asin, view) {
  switch (view?.status) {
    case 'UNKNOWN': return `Spread unknown for ${asin} — try again`;
    case 'NO_BOX': return `Box qty not set for ${asin} — set the box size to size refill units`;
    case 'NO_RANK': return `No sales rank for ${asin} — refill units unknown`;
    case 'OVERSIZE': return 'Oversize: Amazon controls placement';
    default: return `Nothing to refill for ${asin}`;
  }
}

export default function ShipmentPlannerV2() {
  const [params, setParams] = useSearchParams();
  const [source, setSource] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Monotonic request id: a response whose id is no longer current (newer pick, or unmount) is dropped.
  const reqId = useRef(0);
  useEffect(() => () => { reqId.current = -1; }, []);
  const lastKey = useRef(null);
  const keyOf = (src) => `${src.type}:${src.value}`;

  const load = useCallback(async (src) => {
    const id = ++reqId.current;
    const current = () => reqId.current === id;
    setLoading(true); setMessage(null); setLines([]);
    try {
      let built = [];
      if (src.type === 'po') {
        const res = await ApiClient.get(`/api/shipment-planner/supplier-pos/${encodeURIComponent(src.value)}/items`);
        if (!current()) return;
        built = (res.data || []).filter((it) => it.asin).map((it) => ({
          asin: it.asin, description: it.description, ordered: it.ordered, remaining: it.remaining,
          ...splitPoLine(it, it.remaining), spreadLive: it.spreadLive, source: `PO ${src.value}`,
        }));
      } else {
        const res = await ApiClient.get(`/api/po-creation/spread/${encodeURIComponent(src.value)}`);
        if (!current()) return;
        const view = res.data;
        if (src.type === 'asin') {
          const ok = view && view.status === 'OK' ? (view.spreadUnits || 0) : 0;
          const spreadQty = Math.min(src.units, ok);
          built = [{ asin: src.value, description: null, ordered: null, remaining: src.units,
            depthQty: src.units - spreadQty, spreadQty, spreadLive: view, source: 'Manual' }];
        } else {
          const item = refillItem(src.value, view);
          if (!item) {
            setMessage(refillMessage(src.value, view));
            return;
          }
          built = [{ asin: src.value, description: null, ordered: null, remaining: item.spreadQty,
            depthQty: 0, spreadQty: item.spreadQty, spreadLive: item.spreadLive, source: 'Refill regions' }];
        }
      }
      if (!built.length) { setMessage('No lines to plan'); return; }
      let pkgs = {};
      try { pkgs = await fetchPackaging(built.map((l) => l.asin)); } catch (e) { if (current()) toast.error('Could not load box sizes'); }
      if (!current()) return;
      setLines(built.map((l) => ({ ...l, pkg: pkgs[l.asin] || null })));
    } catch (e) {
      if (current()) setMessage('Could not load lines');
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  const pick = useCallback((src) => {
    setSource(src);
    lastKey.current = keyOf(src);
    const next = {};
    if (src.type === 'po') next.po = src.value;
    if (src.type === 'asin') next.asin = src.value;
    if (src.type === 'refill') next.refill = src.value;
    setParams(next, { replace: true });
    load(src);
  }, [load, setParams]);

  // Deep links: read on mount and whenever the URL changes (pick() pre-records its key so it is not loaded twice).
  const paramKey = params.toString();
  useEffect(() => {
    const po = params.get('po'); const asin = params.get('asin'); const refill = params.get('refill');
    let src = null;
    if (po) src = { type: 'po', value: po };
    else if (refill) src = { type: 'refill', value: refill.toUpperCase() };
    else if (asin) src = { type: 'asin', value: asin.toUpperCase() };
    if (!src || lastKey.current === keyOf(src)) return;
    lastKey.current = keyOf(src);
    setSource(src);
    if (src.type !== 'asin') load(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey]);

  // Warehouse roles once per page; per-ASIN refresh keys re-score the options panel after a draw/confirm.
  const [roles, setRoles] = useState({});
  const [rolesFailed, setRolesFailed] = useState(false);
  const [refreshKeys, setRefreshKeys] = useState({});
  useEffect(() => {
    let live = true;
    Promise.resolve(ApiClient.get('/api/shipment-planner/fc-roles'))
      .then((r) => { if (live && r?.data && typeof r.data === 'object' && !Array.isArray(r.data)) setRoles(r.data); })
      .catch(() => { if (live) setRolesFailed(true); });
    return () => { live = false; };
  }, []);
  const bumpRefresh = (asin) => setRefreshKeys((k) => ({ ...k, [asin]: (k[asin] || 0) + 1 }));
  const reloadSpread = async (asin) => {
    try {
      const res = await ApiClient.get(`/api/po-creation/spread/${encodeURIComponent(asin)}`);
      // F5: re-derive the line's spread from the reloaded read, so it drops once regions fill.
      setLines((ls) => ls.map((l) => (l.asin === asin ? { ...l, ...rederiveSpread(l, res.data) } : l)));
    } catch (e) { /* keep the previous read */ }
  };

  const updateLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  // ---- box editor: the same modal + hook PO Creation uses, one ASIN at a time ----
  const [pkgByAsin, setPkgByAsin] = useState({});
  const [shipmentMode, setShipmentMode] = useState('UPS');
  const [isBoth, setIsBoth] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(null);

  const refreshPkg = async (asin) => {
    try {
      const pkgs = await fetchPackaging([asin]);
      setLines((ls) => ls.map((l) => (l.asin === asin ? { ...l, pkg: pkgs[asin] || l.pkg } : l)));
    } catch (e) {
      toast.error('Box saved, but sizes failed to refresh — reload the page.');
    }
  };

  const box = useBoxConfig({
    shipmentMode, packagingByAsin: pkgByAsin, poConfirmDefaultsByAsin: EMPTY_MAP, poPackagingMetaByAsin: EMPTY_MAP,
    setPackagingByAsin: setPkgByAsin, setToast: (m) => toast.error(m),
    setPoConfirmConfirmed: NOOP, setPackagingConfirmedAsin: NOOP,
    isPackagingComplete, fmtLbsShort, computeLoadedBoxWeightLbs, deriveLegacyPackagingFields,
    computeShipmentTotals, boxFormFromCatalogBox, boxCatalogErrorText, unitBoxEntryFromDerived,
    unitBoxMatchesDerived, defaultPackagingStyle,
    onSaved: async () => { if (box.packagingPopoverAsin) await refreshPkg(box.packagingPopoverAsin); },
    requirePersist: true,
  });

  const openBox = async (line) => {
    try {
      const pkgs = await fetchPackaging([line.asin]);
      const row = pkgs[line.asin] || null;
      setPkgByAsin((prev) => ({ ...prev, [line.asin]: packagingEntryFromRow(row) }));
      const m = modeForRow(row);
      setIsBoth(m.both); setShipmentMode(m.mode);
      setPendingOpen({ asin: line.asin, title: line.description || line.asin });
    } catch (e) {
      toast.error(`Could not load the current packaging for ${line.asin} — try again.`);
    }
  };
  useEffect(() => {
    if (!pendingOpen) return;
    box.openPackagingPopover(pendingOpen.asin, pendingOpen.title, null);
    setPendingOpen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpen]);

  return (
    <div className="p-4 space-y-4 text-gray-900 dark:text-gray-100">
      <h1 className="text-xl font-semibold">Shipment Planner V2</h1>
      <SourcePicker source={source} onPick={pick} />
      {loading && <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>}
      {message && <div className="text-sm text-gray-600 dark:text-gray-300">{message}</div>}
      {rolesFailed && <div className="text-xs text-amber-600 dark:text-amber-400">Warehouse roles unavailable</div>}
      {lines.length > 0 && (
        <LinesTable lines={lines} onChange={updateLine} onSetBox={openBox}
          renderActions={(l) => <LineActions line={l} onDrawn={bumpRefresh} />}
          renderOptions={(l) => (
            <OptionsPanel line={l} roles={roles} refreshKey={refreshKeys[l.asin] || 0}
              onConfirmed={reloadSpread} />
          )} />
      )}
      <BoxConfigModal
        packagingPopoverAsin={box.packagingPopoverAsin} packagingPopoverTitle={box.packagingPopoverTitle}
        packagingPopoverQty={box.packagingPopoverQty} packagingForm={box.packagingForm}
        packagingFieldErrors={box.packagingFieldErrors} packagingFormValid={box.packagingFormValid}
        packagingAttemptedSubmit={box.packagingAttemptedSubmit} packagingStyleChoice={box.packagingStyleChoice}
        packagingSaving={box.packagingSaving} packagingLoadedLbs={box.packagingLoadedLbs}
        packagingSummaryLine={packagingSummaryLine} packagingModalActivePkg={box.packagingModalActivePkg}
        packagingModalDerived={box.packagingModalDerived} packagingModalLegacyOnly={box.packagingModalLegacyOnly}
        packagingModalTotals={box.packagingModalTotals} boxCatalog={box.boxCatalog}
        choosePackagingStyle={box.choosePackagingStyle} closePackagingPopover={box.closePackagingPopover}
        applyPackaging={box.applyPackaging} updatePackagingField={box.updatePackagingField} fmtLbsShort={fmtLbsShort}
        newBoxOpen={box.newBoxOpen} newBoxForm={box.newBoxForm} newBoxSaving={box.newBoxSaving}
        editingBoxId={box.editingBoxId} boxError={box.boxError} boxNote={box.boxNote} selectedBox={box.selectedBox}
        updateNewBoxField={box.updateNewBoxField} saveNewBoxToCatalog={box.saveNewBoxToCatalog}
        renderCatalogBoxCard={box.renderCatalogBoxCard} setEditingBoxId={box.setEditingBoxId}
        setNewBoxForm={box.setNewBoxForm} setBoxError={box.setBoxError} setNewBoxOpen={box.setNewBoxOpen}
        setSelectedBoxId={box.setSelectedBoxId}
        primaryActionOnly
        shipmentModePickerVisible={isBoth} shipmentModeChoice={shipmentMode} onChooseShipmentMode={setShipmentMode}
      />
      {box.saveError && box.packagingPopoverAsin && (
        <div data-testid="box-save-error" className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-md rounded-md bg-red-600 px-4 py-2 text-sm text-white shadow-lg">
          {box.saveError}
        </div>
      )}
    </div>
  );
}
